/**
 * Problem marks in the timeline gutter and the overview strip (W2-12a checker-runtime; DESIGN.md §5.10: problems
 * appear "as ticks in the time gutter and the overview strip"; render/types.ts `TimelineDecorationProvider`).
 *
 * One tick per beat that has problems, in the colour and shape of its most severe problem (`problem-fatal`,
 * `problem-error`, `problem-warning`, `problem-tip`), labelled with that problem's message (and how many more are
 * there). Clicking a tick makes it the current problem, shows the Problems tab and goes there. The user preferences
 * `ext.checker.hideTicks` and `ext.checker.hideTipTicks` turn the ticks off, or off for tips.
 * The provider works over any ProblemsService; with the checker's own service it reuses its beat cache.
 */

import type { AppServices } from '../../app/registry/types';
import type { SettingsService } from '../../app/settings/types';
import { SEVERITY_RANK } from '../../core/check/runner';
import type { Problem, Severity } from '../../core/check/types';
import type { TimelineDecoration, TimelineDecorationProvider } from '../../render/types';
import { m } from './checker.messages';
import { problemBeatF64, revealProblem, tempoOf } from './navigation';
import { asCheckerService } from './problemsService';

/** User preferences (Settings > Panels and tools): no ticks at all / no ticks for tips. */
export const HIDE_TICKS_KEY = 'checker.hideTicks';
export const HIDE_TIP_TICKS_KEY = 'checker.hideTipTicks';

export type TickLevel = 'all' | 'warnings' | 'off';

export function tickLevelOf(settings: Pick<SettingsService, 'getExt'>): TickLevel {
  if (settings.getExt(HIDE_TICKS_KEY) === true) return 'off';
  if (settings.getExt(HIDE_TIP_TICKS_KEY) === true) return 'warnings';
  return 'all';
}

/** Whether problems of a severity get ticks at a level. */
export function ticksShow(level: TickLevel, severity: Severity): boolean {
  switch (level) {
    case 'all':
      return true;
    case 'warnings':
      return severity !== 'tip';
    case 'off':
      return false;
  }
}

interface Tick {
  readonly beat: number;
  readonly problems: readonly Problem[];
}

const KIND: Readonly<Record<Severity, TimelineDecoration['kind']>> = {
  fatal: 'problem-fatal',
  error: 'problem-error',
  warning: 'problem-warning',
  tip: 'problem-tip',
};

/** Ticks of a problem list: grouped by beat (1e-6), sorted by beat, most severe problem first in each. */
export function groupTicks(
  problems: readonly Problem[],
  beatOf: (p: Problem) => number | null,
  level: TickLevel,
): Tick[] {
  const byBeat = new Map<number, Problem[]>();
  for (const p of problems) {
    if (!ticksShow(level, p.severity)) continue;
    const b = beatOf(p);
    if (b === null || !Number.isFinite(b)) continue;
    const key = Math.round(b * 1e6) / 1e6;
    const list = byBeat.get(key);
    if (list === undefined) byBeat.set(key, [p]);
    else list.push(p);
  }
  const ticks: Tick[] = [];
  for (const [beat, list] of byBeat) {
    list.sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]);
    ticks.push({ beat, problems: list });
  }
  ticks.sort((a, b) => a.beat - b.beat);
  return ticks;
}

/** Index of the first tick at or after `beat`. */
function lowerBound(ticks: readonly Tick[], beat: number): number {
  let lo = 0;
  let hi = ticks.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if ((ticks[mid] as Tick).beat < beat) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

export function createProblemDecorations(s: AppServices): TimelineDecorationProvider {
  let cache: {
    readonly list: readonly Problem[];
    readonly key: string;
    readonly ticks: readonly Tick[];
  } | null = null;
  let version = 0;
  const bump = (): void => {
    version++;
  };

  const ticks = (): readonly Tick[] => {
    const level = tickLevelOf(s.settings);
    const checker = asCheckerService(s.problems);
    const problems = s.problems.problems();
    const key = `${String(version)}:${level}`;
    if (cache !== null && cache.list === problems && cache.key === key) return cache.ticks;
    const tempo = checker === null ? tempoOf(s, s.store) : null;
    const beatOf = (p: Problem): number | null => (checker !== null ? checker.beatOf(p) : problemBeatF64(p, tempo));
    const list = groupTicks(problems, beatOf, level);
    cache = { list: problems, key, ticks: list };
    return list;
  };

  const decoration = (t: Tick): TimelineDecoration => {
    const top = t.problems[0] as Problem;
    const message = s.i18n.tr(top.message);
    const more = t.problems.length - 1;
    return {
      beat: t.beat,
      kind: KIND[top.severity],
      label: more > 0 ? s.i18n.t(m.tickMore, { message, count: more }) : message,
      onClick: () => {
        asCheckerService(s.problems)?.setCurrent(top.id);
        s.ui.showPanel('problems');
        void revealProblem(s, top);
      },
    };
  };

  return {
    id: 'problems',
    decorations(range) {
      const list = ticks();
      const [from, to] = range[0] <= range[1] ? range : [range[1], range[0]];
      const out: TimelineDecoration[] = [];
      for (let i = lowerBound(list, from); i < list.length; i++) {
        const t = list[i] as Tick;
        if (t.beat > to) break;
        out.push(decoration(t));
      }
      return out;
    },
    subscribe(cb) {
      const a = s.problems.subscribe(() => {
        bump();
        cb();
      });
      const b = s.settings.subscribe(() => {
        bump();
        cb();
      });
      return () => {
        a();
        b();
      };
    },
  };
}
